import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { api, clearTokens, CurrentUser, getToken, setRefresh, setToken } from "./api";

interface AuthCtx {
  user: CurrentUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const me = await api.get<CurrentUser>("/api/v1/auth/me");
      setUser(me);
    } catch {
      clearTokens();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function login(email: string, password: string) {
    const r = await api.post<{ access_token: string; refresh_token: string }>(
      "/api/v1/auth/login",
      { email, password }
    );
    setToken(r.access_token);
    setRefresh(r.refresh_token);
    await refresh();
  }

  function logout() {
    clearTokens();
    setUser(null);
    window.location.href = "/login";
  }

  return (
    <Ctx.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be used within AuthProvider");
  return v;
}

// Role helpers
export function isAdmin(role: string | undefined) {
  return role === "admin" || role === "super_admin";
}
export function isSuper(role: string | undefined) {
  return role === "super_admin";
}
export function isAnalystOrHigher(role: string | undefined) {
  return role === "analyst" || isAdmin(role);
}
